import { useMemo, useState } from 'react';

import {
  BaseRequiredDialogProps,
  Button,
  DialogDraggable,
  neutral,
  Select,
} from '@bloomfieldrobotics/canopy';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import {
  Box,
  DialogContent,
  Stack,
  styled,
  TextareaAutosize,
  Typography,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { uniqueId } from 'lodash';
import LoadingSpinner from '../commonUI/LoadingSpinner';
import GenericDataGrid from '../dataGridTables/GenericDataGrid';
import { queryMap } from './duckDBSlice';
import { useDuckDbQuery } from './third-party-kit/hooks/useDuckDbQuery';

const StyledTextarea = styled(TextareaAutosize)(({ theme }) => ({
  minWidth: '320px',
  width: '100%',
  fontSize: '0.875rem',
  fontWeight: 400,
  lineHeight: 1.5,
  padding: '12px',
  borderRadius: theme.shape.borderRadius,
  color: neutral[300],
  background: neutral[900],
  border: '1px solid',
  borderColor: neutral[700],
}));

export const QueryRunner = ({
  open: isOpen,
  onClose,
}: BaseRequiredDialogProps) => {
  const [displaySql, setDisplaySql] = useState<string>('');
  const [skip, setSkip] = useState(true);
  const { arrow, loading, error, forceUpdate } = useDuckDbQuery(
    displaySql || undefined,
    skip
  );

  const result = useMemo(() => {
    if (!arrow) return null;
    return arrow.toArray().map((row, i) => {
      return { id: i, ...row.toJSON() };
    });
  }, [arrow]);

  const cols: GridColDef[] = useMemo(() => {
    if (!result) return [];
    console.debug('Query result: ', result);
    try {
      if (result.length === 0) return [];
      return Object.keys(result[0]).map((el) => ({ field: el, flex: 1 }));
    } catch (e) {
      console.error('Error: ', e);
      return [];
    }
  }, [result]);

  const content = useMemo(() => {
    return (
      <Box minWidth='500px'>
        <Stack spacing={2} sx={{ marginBottom: 8 }}>
          <Stack direction='row' sx={{ justifyContent: 'space-between' }}>
            <Stack direction='row' spacing={2}>
              <Typography variant='h6'>Quick Queries:</Typography>
              <Select
                value={displaySql}
                onChange={(val) => setDisplaySql(val as string)}
                options={queryMap.map((q) => ({
                  label: q.name,
                  value: q.query,
                }))}
              />
            </Stack>
            <Button
              color='secondary'
              variant='contained'
              onClick={() => {
                setSkip(false);
                forceUpdate();
              }}
              sx={{ alignSelf: 'flex-start' }}
              startIcon={<PlayCircleOutlineIcon />}
            >
              Run
            </Button>
          </Stack>
          <StyledTextarea
            value={displaySql}
            onChange={(e) => {
              setDisplaySql(e.target.value);
              setSkip(true);
            }}
            placeholder='SELECT * from scanData'
            spellCheck='false'
          />
        </Stack>
        {loading && <LoadingSpinner />}
        {error && (
          <Typography sx={{ color: 'red' }}>{error.message}</Typography>
        )}
        {!loading && !error && !!result && (
          <Stack spacing={2}>
            <Typography variant='h6'>Results</Typography>
            <GenericDataGrid
              columns={cols}
              rows={result || []}
              getRowId={() => uniqueId()}
              initialState={{
                pagination: { paginationModel: { pageSize: 5 } },
              }}
              pageSizeOptions={[5, 25, 100]}
            />
          </Stack>
        )}
      </Box>
    );
  }, [result, cols, loading, error, displaySql]);

  return (
    <DialogDraggable
      title={'DuckDB Query Runner'}
      open={isOpen}
      onClose={onClose}
      fullWidth
    >
      <DialogContent>{content}</DialogContent>
    </DialogDraggable>
  );
};
